import { addDays, isValid, parseISO } from "date-fns";

export const MAX_INSTALL_SHIFTS = 14;
export const INSTALL_TIME_ZONE = "America/New_York";

const zonedFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: INSTALL_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

function parseDateIso(dateIso: string): { year: number; month: number; day: number } {
  const [year, month, day] = dateIso.split("-").map(Number);
  return { year, month, day };
}

function formatUtcDateIso(date: Date): string {
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

function getZonedParts(date: Date) {
  const values = Object.fromEntries(
    zonedFormatter.formatToParts(date)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, Number(part.value)]),
  ) as Record<string, number>;

  return {
    year: values.year,
    month: values.month,
    day: values.day,
    hour: values.hour === 24 ? 0 : values.hour,
    minute: values.minute,
    second: values.second,
  };
}

function getTimeZoneOffsetMs(date: Date): number {
  const parts = getZonedParts(date);
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return asUtc - date.getTime();
}

export function createInstallDateTime(
  dateIso: string,
  hour: number,
  minute = 0,
  second = 0,
): Date {
  const { year, month, day } = parseDateIso(dateIso);
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  const firstPass = new Date(utcGuess.getTime() - getTimeZoneOffsetMs(utcGuess));
  return new Date(utcGuess.getTime() - getTimeZoneOffsetMs(firstPass));
}

export function formatInstallDateIso(date: Date): string {
  const parts = getZonedParts(date);
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
}

export function formatInstallDateTimeLabel(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: INSTALL_TIME_ZONE,
    weekday: "long",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
}

export function getInstallClockTime(date: Date): { hour: number; minute: number } {
  const parts = getZonedParts(date);
  return { hour: parts.hour, minute: parts.minute };
}

export function clampInstallShifts(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 1;
  return Math.max(1, Math.min(MAX_INSTALL_SHIFTS, Math.floor(parsed)));
}

export function advanceInstallDay(date: Date): Date {
  const parts = getZonedParts(date);
  let cursor = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  do {
    cursor = addDays(cursor, 1);
  } while (cursor.getUTCDay() === 0 || cursor.getUTCDay() === 6);

  return createInstallDateTime(
    formatUtcDateIso(cursor),
    parts.hour,
    parts.minute,
    parts.second,
  );
}

export function advanceInstallCalendarDay(date: Date): Date {
  const parts = getZonedParts(date);
  const cursor = addDays(new Date(Date.UTC(parts.year, parts.month - 1, parts.day)), 1);
  return createInstallDateTime(
    formatUtcDateIso(cursor),
    parts.hour,
    parts.minute,
    parts.second,
  );
}

function advanceInstallDateIso(dateIso: string): string {
  const { year, month, day } = parseDateIso(dateIso);
  let cursor = new Date(Date.UTC(year, month - 1, day));
  do {
    cursor = addDays(cursor, 1);
  } while (cursor.getUTCDay() === 0 || cursor.getUTCDay() === 6);
  return formatUtcDateIso(cursor);
}

export function getInstallEndForShifts(start: Date, shifts: number): Date {
  const totalShifts = clampInstallShifts(shifts);
  let endDateIso = formatInstallDateIso(start);
  for (let i = 1; i < totalShifts; i += 1) {
    endDateIso = advanceInstallDateIso(endDateIso);
  }
  const end = createInstallDateTime(endDateIso, 17);
  return end > start ? end : new Date(start.getTime() + 8 * 60 * 60 * 1000);
}

export function getInstallCalendarEndForShifts(start: Date, shifts: number): Date {
  const totalShifts = clampInstallShifts(shifts);
  let cursor = new Date(start);
  for (let i = 1; i < totalShifts; i += 1) {
    cursor = advanceInstallCalendarDay(cursor);
  }
  const endDayIso = formatInstallDateIso(cursor);
  const end = createInstallDateTime(endDayIso, 17);
  return end > start ? end : new Date(start.getTime() + 8 * 60 * 60 * 1000);
}

export function getInstallShiftCountFromRange(startIso?: string | null, endIso?: string | null): number {
  if (!startIso || !endIso) return 1;

  const start = parseISO(startIso);
  const end = parseISO(endIso);
  if (!isValid(start) || !isValid(end) || end <= start) return 1;

  const endDayIso = formatInstallDateIso(end);
  let cursor = new Date(start);
  let shifts = 1;

  while (shifts < MAX_INSTALL_SHIFTS && formatInstallDateIso(cursor) !== endDayIso) {
    cursor = advanceInstallDay(cursor);
    shifts += 1;
    if (cursor > end && formatInstallDateIso(cursor) !== endDayIso) break;
  }

  return clampInstallShifts(shifts);
}

export function getInstallCalendarShiftCountFromRange(startIso?: string | null, endIso?: string | null): number {
  if (!startIso || !endIso) return 1;

  const start = parseISO(startIso);
  const end = parseISO(endIso);
  if (!isValid(start) || !isValid(end) || end <= start) return 1;

  const endDayIso = formatInstallDateIso(end);
  let cursor = new Date(start);
  let shifts = 1;

  while (shifts < MAX_INSTALL_SHIFTS && formatInstallDateIso(cursor) !== endDayIso) {
    cursor = advanceInstallCalendarDay(cursor);
    shifts += 1;
    if (cursor > end && formatInstallDateIso(cursor) !== endDayIso) break;
  }

  return clampInstallShifts(shifts);
}
